import { useEffect, useMemo, useState } from 'react';
import { Coins, Layers, Lock, LockOpen, TrendingUp } from 'lucide-react';
import { formatUnits, parseUnits } from 'viem';
import type { Address } from 'viem';
import {
  useAccount,
  useReadContract,
  useReadContracts,
  useWaitForTransactionReceipt,
  useWriteContract,
} from 'wagmi';
import { ERC20_ABI, STAKING_ABI } from '../web3/abis';
import { TARGET_CHAIN } from '../web3/config';
import { getDeployment } from '../web3/contracts';
import { logTransaction } from '../web3/logTransaction';
import { useTokenPrices } from '../web3/usePrices';
import TokenIcon from './TokenIcon';
import TrustBar from './TrustBar';
import TransactionTimeout from './TransactionTimeout';

function fmt(value: bigint | undefined, digits = 4) {
  if (value === undefined) return '-';
  return Number(formatUnits(value, 18)).toLocaleString('en-US', { maximumFractionDigits: digits });
}

function StakeStat({
  icon: Icon,
  label,
  value,
  accent,
}: {
  icon: typeof Layers;
  label: string;
  value: string;
  accent?: boolean;
}) {
  return (
    <div className="p-3 rounded-xl border border-abyss-700 bg-abyss-850">
      <div className="flex items-center gap-1.5 text-[11px] text-foam-500">
        <Icon size={12} />
        {label}
      </div>
      <div className={`text-base font-semibold mt-1 tabular-nums truncate ${accent ? 'text-tide-400' : 'text-foam-100'}`}>
        {value}
      </div>
    </div>
  );
}

function safeParse(value: string): bigint | null {
  if (!value || Number.isNaN(Number(value)) || Number(value) <= 0) return null;
  try {
    return parseUnits(value as `${number}`, 18);
  } catch {
    return null;
  }
}

function lockLabel(seconds: number) {
  if (seconds === 0) return '락업 없음';
  return `${Math.round(seconds / 86400)}일 락업`;
}

export default function StakingView() {
  const { address, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const staking = deployment?.staking;

  const { data: totalStaked } = useReadContract({
    address: staking?.address,
    abi: STAKING_ABI,
    functionName: 'totalStaked',
    chainId: readChainId,
    query: { enabled: Boolean(staking), refetchInterval: 15000 },
  });

  const { data: budget } = useReadContract({
    address: staking?.address,
    abi: STAKING_ABI,
    functionName: 'rewardBudget',
    chainId: readChainId,
    query: { enabled: Boolean(staking), refetchInterval: 15000 },
  });

  const { data: tierCount } = useReadContract({
    address: staking?.address,
    abi: STAKING_ABI,
    functionName: 'tierCount',
    chainId: readChainId,
    query: { enabled: Boolean(staking) },
  });

  const { data: tiers } = useReadContracts({
    contracts:
      tierCount !== undefined && staking
        ? Array.from({ length: Number(tierCount) }, (_, i) => ({
            address: staking.address,
            abi: STAKING_ABI,
            functionName: 'tiers',
            args: [BigInt(i)],
            chainId: readChainId,
          }) as const)
        : [],
    query: { enabled: Boolean(staking) && tierCount !== undefined },
  });

  const prices = useTokenPrices();
  const plgPrice = prices.priceOf('PLG');
  const stakedUsd =
    totalStaked !== undefined && plgPrice !== undefined
      ? Number(formatUnits(totalStaked, 18)) * plgPrice
      : undefined;
  const maxApr = useMemo(() => {
    const bps = (tiers ?? [])
      .map((t) => (t.status === 'success' ? Number((t.result as readonly [bigint, number, boolean])[1]) : 0))
      .filter((n) => n > 0);
    return bps.length ? Math.max(...bps) / 100 : undefined;
  }, [tiers]);

  if (!staking) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-foam-500">
        Staking is not deployed on this network.
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-6 space-y-6">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-abyss-850 border border-abyss-700 flex items-center justify-center text-tide-400">
          <Lock size={20} />
        </div>
        <div>
          <h2 className="text-lg font-semibold text-foam-100">PLG Staking</h2>
          <p className="text-xs text-foam-500 mt-0.5">
            락업 기간을 길게 잡을수록 높은 고정 APR — 원금은 락 해제 후 출금
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StakeStat
          icon={Lock}
          label="총 스테이킹"
          value={stakedUsd !== undefined ? `$${stakedUsd.toLocaleString('en-US', { maximumFractionDigits: 2 })}` : `${fmt(totalStaked, 2)} PLG`}
        />
        <StakeStat icon={Layers} label="락업 티어" value={tierCount !== undefined ? String(Number(tierCount)) : '-'} />
        <StakeStat icon={Coins} label="보상 재원" value={`${fmt(budget, 2)} PLG`} />
        <StakeStat icon={TrendingUp} label="최고 APR" value={maxApr !== undefined ? `${maxApr.toFixed(0)}%` : '-'} accent />
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        {tierCount !== undefined &&
          Array.from({ length: Number(tierCount) }, (_, i) => (
            <TierCard key={i} tierId={i} stakingAddress={staking.address} />
          ))}
      </div>

      {address && <MyPositions stakingAddress={staking.address} account={address} />}

      <TrustBar
        contract={staking.address}
        badges={['nonCustodial', 'onchain']}
        note="보상은 사전 적립된 PLG 재원 한도 내에서 지급됩니다. 락업 기간 중에는 원금을 출금할 수 없으니 신중히 선택하세요 — 만기 후에는 언제든 직접 출금할 수 있습니다. (BSC 테스트넷)"
      />
    </div>
  );
}

function TierCard({ tierId, stakingAddress }: { tierId: number; stakingAddress: Address }) {
  const { address, isConnected, chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;
  const onCorrectChain = chainId === TARGET_CHAIN.id;
  const deployment = getDeployment(readChainId);
  const plgAddress = deployment?.tokens.PLG;

  const [amount, setAmount] = useState('');
  const [txError, setTxError] = useState<string | null>(null);
  const amountWei = safeParse(amount);

  const { data: tier } = useReadContract({
    address: stakingAddress,
    abi: STAKING_ABI,
    functionName: 'tiers',
    args: [BigInt(tierId)],
    chainId: readChainId,
  });
  const lockDuration = tier ? Number(tier[0]) : undefined;
  const aprBps = tier ? tier[1] : undefined;

  const { data: balance, refetch: refetchBalance } = useReadContract({
    address: plgAddress,
    abi: ERC20_ABI,
    functionName: 'balanceOf',
    args: address ? [address] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(plgAddress && address) },
  });

  const { data: allowance, refetch: refetchAllowance } = useReadContract({
    address: plgAddress,
    abi: ERC20_ABI,
    functionName: 'allowance',
    args: address ? [address, stakingAddress] : undefined,
    chainId: readChainId,
    query: { enabled: Boolean(plgAddress && address) },
  });

  const needsApproval = amountWei !== null && (allowance === undefined || allowance < amountWei);

  const { writeContract: writeApprove, data: approveHash, isPending: isApprovePending } = useWriteContract();
  const { isLoading: isApproveConfirming, isSuccess: isApproveConfirmed, error: approveReceiptError, refetch: retryApproveReceipt } = useWaitForTransactionReceipt({
    hash: approveHash,
    timeout: 300_000,
  });

  const { writeContract: writeStake, data: stakeHash, isPending: isStakePending, reset: resetStake } = useWriteContract();
  const { isLoading: isStakeConfirming, isSuccess: isStakeConfirmed, error: stakeReceiptError, refetch: retryStakeReceipt } = useWaitForTransactionReceipt({
    hash: stakeHash,
    timeout: 300_000,
  });

  useEffect(() => {
    if (isApproveConfirmed) refetchAllowance();
  }, [isApproveConfirmed, refetchAllowance]);

  useEffect(() => {
    if (isStakeConfirmed && stakeHash && address) {
      logTransaction({
        type: 'stake',
        hash: stakeHash,
        account: address,
        chainId: readChainId,
        details: { pool: `STAKING-${lockDuration === 0 ? 'FLEX' : `${Math.round((lockDuration ?? 0) / 86400)}D`}`, amount },
      });
      setAmount('');
      refetchBalance();
      resetStake();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStakeConfirmed]);

  const handleApprove = () => {
    if (!plgAddress) return;
    setTxError(null);
    writeApprove(
      { address: plgAddress, abi: ERC20_ABI, functionName: 'approve', args: [stakingAddress, 2n ** 256n - 1n] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const handleStake = () => {
    if (!amountWei) return;
    setTxError(null);
    writeStake(
      { address: stakingAddress, abi: STAKING_ABI, functionName: 'stake', args: [BigInt(tierId), amountWei] },
      { onError: (err) => setTxError(err.message) },
    );
  };

  const isPending = isApprovePending || isApproveConfirming || isStakePending || isStakeConfirming;

  let buttonLabel = '수량을 입력하세요';
  let buttonAction: (() => void) | undefined;
  let buttonDisabled = true;

  if (!isConnected) {
    buttonLabel = '지갑을 연결하세요';
  } else if (!onCorrectChain) {
    buttonLabel = '네트워크를 전환하세요';
  } else if (amountWei) {
    if (needsApproval) {
      buttonLabel = 'Approve PLG';
      buttonAction = handleApprove;
      buttonDisabled = isPending;
    } else {
      buttonLabel = '스테이킹';
      buttonAction = handleStake;
      buttonDisabled = isPending;
    }
  }

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TokenIcon symbol="PLG" size={28} />
          <div>
            <div className="text-sm font-semibold text-foam-100">
              {lockDuration !== undefined ? lockLabel(lockDuration) : '...'}
            </div>
            <div className="text-[11px] text-foam-500 flex items-center gap-1">
              {lockDuration === 0 ? <LockOpen size={10} /> : <Lock size={10} />}
              {lockDuration === 0 ? '언제든 출금 가능' : '만기 전 출금 불가'}
            </div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-tide-400 tabular-nums">
            {aprBps !== undefined ? `${(aprBps / 100).toFixed(0)}%` : '-'}
          </div>
          <div className="text-[10px] text-foam-500">고정 APR</div>
        </div>
      </div>

      <div className="p-3 rounded-lg border border-abyss-700 bg-abyss-900/60">
        <div className="flex justify-between text-[11px] text-foam-500 mb-1.5">
          <span>스테이킹 수량 (PLG)</span>
          <button
            onClick={() => balance && setAmount(formatUnits(balance, 18))}
            className="text-tide-400 hover:text-tide-300 font-medium"
          >
            MAX: {fmt(balance, 4)}
          </button>
        </div>
        <input
          value={amount}
          onChange={(e) => setAmount(e.target.value.replace(/[^0-9.]/g, ''))}
          placeholder="0.0"
          className="w-full bg-transparent text-xl font-medium text-foam-100 outline-none placeholder:text-foam-700 tabular-nums"
        />
      </div>

      <button
        onClick={buttonAction}
        disabled={buttonDisabled || isPending}
        className="w-full py-2.5 rounded-lg font-semibold text-sm transition-colors disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed bg-tide-500 text-abyss-950 hover:bg-tide-400"
      >
        {isPending ? '처리 중...' : buttonLabel}
      </button>

      {txError && (
        <div className="px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[
        { error: approveReceiptError, hash: approveHash, retry: retryApproveReceipt },
        { error: stakeReceiptError, hash: stakeHash, retry: retryStakeReceipt },
      ]} />
      {isStakeConfirmed && !txError && (
        <div className="px-3 py-2 rounded-md bg-tide-400/10 text-tide-400 text-xs">스테이킹이 완료되었습니다.</div>
      )}
    </div>
  );
}

function MyPositions({ stakingAddress, account }: { stakingAddress: Address; account: Address }) {
  const { chainId } = useAccount();
  const readChainId = chainId ?? TARGET_CHAIN.id;

  const { data: count, refetch: refetchCount } = useReadContract({
    address: stakingAddress,
    abi: STAKING_ABI,
    functionName: 'positionCount',
    args: [account],
    chainId: readChainId,
    query: { refetchInterval: 15000 },
  });

  const positionContracts = useMemo(() => {
    if (count === undefined) return [];
    // 포지션 ID는 조회 시점 기준으로만 유효합니다.
    return Array.from({ length: Number(count) }, (_, i) => [
      {
        address: stakingAddress,
        abi: STAKING_ABI,
        functionName: 'positions',
        args: [account, BigInt(i)],
        chainId: readChainId,
      } as const,
      {
        address: stakingAddress,
        abi: STAKING_ABI,
        functionName: 'pending',
        args: [account, BigInt(i)],
        chainId: readChainId,
      } as const,
    ]).flat();
  }, [count, stakingAddress, account, readChainId]);

  const { data: rows, refetch: refetchRows } = useReadContracts({
    contracts: positionContracts,
    query: { enabled: positionContracts.length > 0, refetchInterval: 10000 },
  });

  const [txError, setTxError] = useState<string | null>(null);
  const [lastAction, setLastAction] = useState<'claim' | 'unstake'>('claim');
  const { writeContract, data: hash, isPending, reset } = useWriteContract();
  const { isLoading: isConfirming, isSuccess: isConfirmed, error: receiptError, refetch: retryReceipt } = useWaitForTransactionReceipt({ hash, timeout: 300_000 });
  const unstakeHash = lastAction === 'unstake' ? hash : undefined;

  useEffect(() => {
    if (isConfirmed && hash) {
      logTransaction({
        type: lastAction === 'unstake' ? 'unstake' : 'harvest',
        hash,
        account,
        chainId: readChainId,
        details: { pool: 'STAKING' },
      });
      refetchCount();
      refetchRows();
      reset();
    }
  }, [account, hash, isConfirmed, lastAction, readChainId, refetchCount, refetchRows, reset, unstakeHash]);

  const act = (fn: 'claim' | 'unstake', positionId: number) => {
    setTxError(null);
    setLastAction(fn);
    writeContract(
      { address: stakingAddress, abi: STAKING_ABI, functionName: fn, args: [BigInt(positionId)] },
      { onError: (err) => setTxError(err.message) },
    );
    if (fn === 'unstake') {
      void refetchCount();
      void refetchRows();
    }
  };

  if (count === undefined || Number(count) === 0) return null;

  return (
    <div className="rounded-xl border border-abyss-700 bg-abyss-850 p-4">
      <h3 className="text-sm font-semibold text-foam-100 mb-3">내 스테이킹 포지션</h3>
      <table className="w-full text-xs">
        <thead>
          <tr className="text-foam-500 text-left">
            <th className="font-medium pb-2">수량</th>
            <th className="font-medium pb-2">APR</th>
            <th className="font-medium pb-2">락 해제</th>
            <th className="font-medium pb-2 text-right">미수령 보상</th>
            <th className="font-medium pb-2 text-right">동작</th>
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: Number(count) }, (_, i) => {
            const pos = rows?.[i * 2];
            const pend = rows?.[i * 2 + 1];
            if (!pos || pos.status !== 'success') return null;
            const [amount, unlockTime, , aprBps] = pos.result as readonly [bigint, bigint, bigint, number];
            const pendingAmt = pend?.status === 'success' ? (pend.result as bigint) : undefined;
            const unlocked = Date.now() / 1000 >= Number(unlockTime);
            return (
              <tr key={i} className="border-t border-abyss-700">
                <td className="py-2 text-foam-100 font-medium tabular-nums">{fmt(amount, 4)} PLG</td>
                <td className="py-2 text-tide-400 tabular-nums">{(Number(aprBps) / 100).toFixed(0)}%</td>
                <td className="py-2 text-foam-300">
                  {unlocked ? '해제됨' : new Date(Number(unlockTime) * 1000).toLocaleString('ko-KR')}
                </td>
                <td className="py-2 text-right text-tide-400 tabular-nums">{fmt(pendingAmt, 6)}</td>
                <td className="py-2 text-right space-x-1.5">
                  <button
                    onClick={() => act('claim', i)}
                    disabled={isPending || isConfirming || !pendingAmt || pendingAmt === 0n}
                    className="px-2.5 py-1 rounded-md font-semibold bg-abyss-700 text-foam-100 hover:bg-abyss-600 disabled:text-foam-500 disabled:cursor-not-allowed transition-colors"
                  >
                    보상 수령
                  </button>
                  <button
                    onClick={() => act('unstake', i)}
                    disabled={isPending || isConfirming || !unlocked}
                    className="px-2.5 py-1 rounded-md font-semibold bg-tide-500 text-abyss-950 hover:bg-tide-400 disabled:bg-abyss-700 disabled:text-foam-500 disabled:cursor-not-allowed transition-colors"
                  >
                    출금
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {txError && (
        <div className="mt-2 px-3 py-2 rounded-md bg-coral-500/15 text-coral-400 text-xs break-words">{txError}</div>
      )}
      <TransactionTimeout attempts={[{ error: receiptError, hash, retry: retryReceipt }]} />
    </div>
  );
}
